import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MCQScene } from '@/types/gameSpec';
import type { SceneComponentProps } from './sceneCommon';
import { shuffle, SCENE_LABEL } from './sceneCommon';

export const MCQSceneView = ({ scene, onAnswer }: SceneComponentProps<MCQScene>) => {
  const [selected, setSelected] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);

  // Shuffle once per scene; remember the mapping back to the original index.
  const shuffledIndices = useMemo(
    () => shuffle(scene.options.map((_, i) => i)),
    [scene.id],
  );

  const handlePick = (origIdx: number) => {
    if (revealed) return;
    setSelected(origIdx);
    setRevealed(true);
    const isCorrect = origIdx === scene.correct_index;
    // give the learner a beat to see the result
    setTimeout(() => onAnswer(isCorrect, { selected_index: origIdx }), 700);
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {SCENE_LABEL.mcq}
        </div>
        <p className="text-lg font-medium">{scene.prompt}</p>

        <div className="grid gap-2 sm:grid-cols-2">
          {shuffledIndices.map((origIdx) => {
            const isPicked = selected === origIdx;
            const isCorrectOption = origIdx === scene.correct_index;
            const showCorrect = revealed && isCorrectOption;
            const showWrong = revealed && isPicked && !isCorrectOption;
            return (
              <Button
                key={origIdx}
                variant="outline"
                className={cn(
                  'h-auto py-3 px-4 justify-start text-left whitespace-normal',
                  showCorrect && 'border-green-500 bg-green-50 text-green-900',
                  showWrong && 'border-destructive bg-red-50 text-red-900',
                )}
                onClick={() => handlePick(origIdx)}
                disabled={revealed}
              >
                <span className="flex-1">{scene.options[origIdx]}</span>
                {showCorrect && <CheckCircle2 className="h-4 w-4 ml-2" />}
                {showWrong && <XCircle className="h-4 w-4 ml-2" />}
              </Button>
            );
          })}
        </div>

        {scene.hint && !revealed && (
          <p className="text-sm text-muted-foreground">💡 {scene.hint}</p>
        )}
        {revealed && scene.explanation && (
          <p className="text-sm text-muted-foreground border-t pt-2">
            {scene.explanation}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
